import { Layout } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Shield, Award, Zap } from 'lucide-react';
import { productsData } from '@/data/productsData';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const CreatinePage = () => {
  const creatineProducts = productsData.filter(p => p.category === 'creatine');

  return (
    <>
      <Helmet>
        <title>Buy Creatine Monohydrate in India & Nepal – Pure & Authentic | NutraFuel®</title>
        <meta name="description" content="Buy 100% pure creatine monohydrate in India & Nepal. Micronized, lab-tested, authentic creatine for strength, power & muscle growth. Fast delivery across India." />
        <meta name="keywords" content="buy creatine india, creatine monohydrate nepal, pure creatine india, micronized creatine, creatine supplements india nepal" />
        <link rel="canonical" href="https://nutrafuel.in/creatine" />
        <meta property="og:title" content="Buy Creatine Monohydrate in India & Nepal – Pure & Authentic" />
        <meta property="og:description" content="100% pure creatine monohydrate for strength and muscle growth." />
        <meta property="og:url" content="https://nutrafuel.in/creatine" />
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground font-medium">Creatine</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-primary text-primary-foreground">Strength & Power</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                Buy Creatine Monohydrate in India & Nepal
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Shop 100% pure, micronized creatine monohydrate supplements in India and Nepal. Lab-tested for purity, trusted by athletes for explosive strength gains and muscle volumization.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">100% Pure</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600">
                  <Award className="w-4 h-4" />
                  <span className="text-sm font-medium">Lab Tested</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-medium">Proven Results</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-display font-bold text-foreground mb-8">
              Premium Creatine Products
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
              {creatineProducts.map((product) => (
                <Link key={product.id} to={`/products/${product.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-2 flex flex-col h-full">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <img
                        src={product.image}
                        alt={`${product.name} - Pure creatine monohydrate supplement`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      {product.popular && (
                        <Badge className="absolute top-3 left-3 bg-gold text-foreground">
                          Popular
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <span className="text-xs text-gold font-medium uppercase tracking-wider mb-1">{product.brand}</span>
                      <h3 className="font-bold text-foreground mb-2">{product.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1">{product.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {product.features.map((feature) => (
                          <span key={feature} className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* SEO Content */}
            <div className="max-w-4xl mx-auto">
              <article className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  What is Creatine Monohydrate?
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Creatine monohydrate is one of the most researched and effective sports supplements available in India and Nepal. It naturally occurs in muscle cells and helps produce energy during high-intensity exercise. Supplementing with creatine increases your muscles' phosphocreatine stores, allowing you to produce more ATP energy for explosive movements.
                </p>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Benefits of Creatine for Indian & Nepalese Athletes
                </h2>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong>Increases Strength & Power:</strong> Research shows 5-15% increase in maximum strength and power output within 4-8 weeks.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong>Enhances Muscle Growth:</strong> Promotes cellular hydration and muscle volumization, creating an optimal environment for muscle protein synthesis.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong>Improves High-Intensity Performance:</strong> Perfect for powerlifting, sprinting, and explosive movements during gym training.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong>Accelerates Recovery:</strong> Reduces muscle damage and inflammation, helping you train harder and more frequently.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong>Brain Health Support:</strong> Emerging research shows benefits for cognitive function and mental clarity.
                    </div>
                  </li>
                </ul>

                <div className="bg-primary/5 border-l-4 border-primary rounded p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">Recommended Stack:</h4>
                  <p className="text-muted-foreground">
                    For maximum muscle and strength gains, combine creatine monohydrate with <Link to="/whey-protein" className="text-primary hover:underline">whey protein</Link> post-workout. Add a <Link to="/pre-workout" className="text-primary hover:underline">pre-workout supplement</Link> for enhanced training intensity.
                  </p>
                </div>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  How to Take Creatine: Dosage & Timing
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-4">
                  The most effective creatine protocol for athletes in India and Nepal is simple:
                </p>

                <div className="bg-muted/50 rounded-lg p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-4">Standard Dosage Protocol:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                    <li><strong>Loading Phase (Optional):</strong> 20g per day (4 servings of 5g) for 5-7 days</li>
                    <li><strong>Maintenance Phase:</strong> 3-5g per day, every day</li>
                    <li><strong>Best Timing:</strong> Post-workout with carbs/protein, or any consistent time daily</li>
                    <li><strong>Hydration:</strong> Drink at least 3-4 liters of water daily</li>
                  </ol>
                </div>

                {/* FAQ Section */}
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Frequently Asked Questions About Creatine
                </h2>

                <div className="space-y-6">
                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Is creatine safe for daily use in India?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Yes, creatine monohydrate is one of the safest and most extensively studied supplements. Decades of research confirm its safety for healthy individuals. At NutraFuel, all our creatine products are pure, tested, and safe for daily consumption in Indian and Nepalese climates. Stay well-hydrated.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Does creatine cause hair loss or kidney damage?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      No, these are myths. Scientific studies show no link between creatine and hair loss or kidney damage in healthy individuals. Creatine is safe when used at recommended doses (3-5g daily). Read our detailed guide: <Link to="/blog/is-creatine-safe" className="text-primary hover:underline">Is Creatine Safe? Benefits, Myths & How to Use It</Link>.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Which is better: creatine monohydrate or creatine HCL?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Creatine monohydrate is the most researched and proven form. It's more affordable and effective. While creatine HCL claims better absorption, monohydrate has decades of scientific backing. We recommend micronized creatine monohydrate for best results and value.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      When will I see results from creatine?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Most users notice increased strength and workout performance within 1-2 weeks. Visible muscle fullness appears within 2-4 weeks. Maximum benefits typically occur after 4-8 weeks of consistent use. Combine with proper training and <Link to="/whey-protein" className="text-primary hover:underline">whey protein</Link> for optimal results.
                    </p>
                  </div>

                  <div className="pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Do I need a loading phase for creatine?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Loading phase (20g/day for 5-7 days) saturates muscles faster but isn't mandatory. You can skip loading and take 3-5g daily – you'll reach the same saturation in 3-4 weeks. Both methods work equally well long-term.
                    </p>
                  </div>
                </div>

                {/* Related Categories */}
                <div className="mt-12 pt-8 border-t border-border">
                  <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                    Maximize Your Results
                  </h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link to="/whey-protein" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Whey Protein</h4>
                      <p className="text-sm text-muted-foreground">Combine with protein for muscle growth</p>
                    </Link>
                    <Link to="/pre-workout" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Pre-Workout</h4>
                      <p className="text-sm text-muted-foreground">Energy & focus for intense training</p>
                    </Link>
                    <Link to="/products" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">All Products</h4>
                      <p className="text-sm text-muted-foreground">Browse complete range</p>
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-display font-bold text-foreground mb-4">
              Questions About Creatine?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Chat with our fitness experts on WhatsApp for personalized creatine recommendations.
            </p>
            <Button variant="default" size="lg" className="bg-green-600 hover:bg-green-700" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I%20have%20questions%20about%20creatine" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5 mr-2" />
                Get Expert Advice
              </a>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default CreatinePage;
